/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * On desktop mice it also drives a hover magnifier: [data-zoom-lens] tracks the
 * pointer over a [data-zoom-src] trigger and [data-zoom-pane] shows that area
 * enlarged in a window over the product details ([data-product-details] beside the gallery).
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    this.#magnifyQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)');
    this.addEventListener('pointermove', this.#handleMagnify, opts);
    this.addEventListener('pointerleave', this.#hideMagnifier, opts);
    window.addEventListener('scroll', this.#hideMagnifier, { ...opts, passive: true });
  }

  #lens = null;
  #pane = null;
  #magnifyQuery = null;
  #zoom = 2.5;

  #handleMagnify = (event) => {
    // Look the lens and window up on each move so editor re-renders of the gallery markup are picked up.
    this.#lens = this.querySelector('[data-zoom-lens]');
    this.#pane = this.querySelector('[data-zoom-pane]');
    if (!this.#lens || !this.#pane || event.pointerType !== 'mouse' || !this.#magnifyQuery?.matches) return;
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-zoom-src]');
    const image = trigger?.querySelector('img');
    if (!trigger || !this.contains(trigger) || !(image instanceof HTMLImageElement) || !image.naturalWidth) {
      this.#hideMagnifier();
      return;
    }

    const host = this.getBoundingClientRect();
    const box = trigger.getBoundingClientRect();
    const details = this.parentElement?.querySelector(':scope > [data-product-details]') ?? null;
    const gap = details ? details.getBoundingClientRect().left - host.right : 48;
    // Square window, as wide as the details column but never taller than the screen.
    let paneWidth = details ? details.getBoundingClientRect().width : box.width;
    let paneHeight = paneWidth;
    if (paneHeight > window.innerHeight - 32) {
      paneHeight = window.innerHeight - 32;
      paneWidth = paneHeight;
    }

    // The photo uses object-cover, so map the pointer onto the visible crop of the natural image.
    const ratio = image.naturalWidth / image.naturalHeight;
    let shownWidth = box.width;
    let shownHeight = box.width / ratio;
    if (shownHeight < box.height) {
      shownHeight = box.height;
      shownWidth = box.height * ratio;
    }
    const cropX = (shownWidth - box.width) / 2;
    const cropY = (shownHeight - box.height) / 2;

    const zoom = this.#zoom;
    const lensWidth = Math.min(box.width, paneWidth / zoom);
    const lensHeight = Math.min(box.height, paneHeight / zoom);
    const pointerX = event.clientX - box.left;
    const pointerY = event.clientY - box.top;
    const lensX = Math.max(0, Math.min(box.width - lensWidth, pointerX - lensWidth / 2));
    const lensY = Math.max(0, Math.min(box.height - lensHeight, pointerY - lensHeight / 2));

    Object.assign(this.#lens.style, {
      left: `${box.left - host.left + lensX}px`,
      top: `${box.top - host.top + lensY}px`,
      width: `${lensWidth}px`,
      height: `${lensHeight}px`,
    });

    // Keep the window beside the hovered photo, inside the visible part of the screen.
    const paneTop = Math.max(16 - host.top, Math.min(box.top - host.top, window.innerHeight - 16 - paneHeight - host.top));
    const source = trigger.getAttribute('data-zoom-src');
    if (this.#pane.dataset.src !== source) {
      this.#pane.dataset.src = source;
      this.#pane.style.backgroundImage = `url("${source}")`;
    }
    Object.assign(this.#pane.style, {
      left: `${host.width + gap}px`,
      top: `${paneTop}px`,
      width: `${paneWidth}px`,
      height: `${paneHeight}px`,
      backgroundSize: `${shownWidth * zoom}px ${shownHeight * zoom}px`,
      backgroundPosition: `${-(cropX + lensX) * zoom}px ${-(cropY + lensY) * zoom}px`,
    });

    this.#lens.toggleAttribute('data-active', true);
    this.#pane.toggleAttribute('data-active', true);
  };

  #hideMagnifier = () => {
    this.#lens?.removeAttribute('data-active');
    this.#pane?.removeAttribute('data-active');
  };

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
